import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  createSaTenant,
  resendSaOwnerInvite,
  SaTenantHealth,
} from "../../../helpers/super_admin_ops_helper";
import {
  getSuperAdminPlans,
  SaPlan,
} from "../../../helpers/super_admin_plans_helper";

/** Senha temporária legível o bastante para Roberto copiar e passar ao cliente. */
function generateTempPassword(length = 12): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

/**
 * Criar cliente no SA (LAC-053): owner + plano + canal, sem gateway.
 */
const SuperAdminTenantNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [plans, setPlans] = useState<SaPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<SaTenantHealth | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  /** LAC-193: create usou convite — tela done não mostra senha. */
  const [createdViaInvite, setCreatedViaInvite] = useState(false);
  const [inviteMailFailed, setInviteMailFailed] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [inviteAcceptUrl, setInviteAcceptUrl] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState(() => generateTempPassword());
  const [showPassword, setShowPassword] = useState(false);
  // LAC-074: se true, dispara convite por e-mail em vez de definir senha.
  const [sendInvite, setSendInvite] = useState(false);
  const [planId, setPlanId] = useState<number | "">("");
  const [billingChannel, setBillingChannel] = useState<"brdominio" | "self_serve">(
    "brdominio"
  );
  // Nome ≠ `document` — senão sombreia o Document do browser e quebra `document.title` no build.
  const [taxDocument, setTaxDocument] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  document.title = t("sa.tenants.new.documentTitle");

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === planId) || null,
    [plans, planId]
  );

  // LAC-062: retorna badge de visibilidade do plano.
  const visibilityBadge = (visibility: string | undefined) => {
    if (visibility === "private")
      return (
        <Badge color="warning" className="ms-1">
          {t("sa.plans.visibility.private")}
        </Badge>
      );
    if (visibility === "internal")
      return (
        <Badge color="info" className="ms-1">
          {t("sa.plans.visibility.internal")}
        </Badge>
      );
    return null; // public = sem badge, é padrão.
  };

  // LAC-062: confirma atribuição de plano privado.
  const handlePlanChange = (newPlanId: number | "") => {
    if (typeof newPlanId !== "number") {
      setPlanId(newPlanId);
      return;
    }
    const plan = plans.find((p) => p.id === newPlanId);
    if (plan?.visibility === "private") {
      if (
        !window.confirm(
          t("sa.plans.confirmPrivate", { name: plan.name })
        )
      ) {
        return;
      }
    }
    setPlanId(newPlanId);
  };

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const res = await getSuperAdminPlans({ is_active: true, per_page: 100 });
      const list = (res?.data || []).filter((p) => p.is_active);
      setPlans(list);
      if (list.length > 0 && planId === "") {
        setPlanId(list[0].id);
      }
    } catch {
      setError(t("sa.tenants.new.error.plans"));
    } finally {
      setLoadingPlans(false);
    }
  }, [planId, t]);

  useEffect(() => {
    loadPlans();
    // Só no mount — planId default é preenchido uma vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyText = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast.success(t("sa.tenants.new.copied"), { autoClose: 1500 });
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error(t("sa.tenants.new.copyFailed"), { autoClose: 2500 });
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t("sa.tenants.new.errors.name");
    if (!ownerName.trim()) next.owner_name = t("sa.tenants.new.errors.ownerName");
    if (!ownerEmail.trim()) next.owner_email = t("sa.tenants.new.errors.ownerEmail");
    // LAC-074: senha só obrigatória se não enviar convite.
    if (!sendInvite && ownerPassword.trim().length < 8) {
      next.owner_password = t("sa.tenants.new.errors.ownerPassword");
    }
    if (!planId) next.plan_id = t("sa.tenants.new.errors.plan");
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || typeof planId !== "number") return;

    setSaving(true);
    setError(null);
    try {
      // LAC-074: monta payload com ou sem password dependendo do modo de convite.
      const payload: Parameters<typeof createSaTenant>[0] = {
        name: name.trim(),
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim().toLowerCase(),
        plan_id: planId,
        billing_channel: billingChannel,
        document: taxDocument.trim() || null,
        phone_number: phoneNumber.trim() || null,
        send_invite: sendInvite,
      };
      // Só envia senha se não for convite.
      if (!sendInvite) {
        payload.owner_password = ownerPassword;
      }
      const res = await createSaTenant(payload);
      if (!res?.success || !res.data) {
        setError(t("sa.tenants.new.error.save"));
        return;
      }
      setCreated(res.data);
      setCreatedViaInvite(sendInvite);
      const mailFailed = Boolean(res.data.invite_mail?.failed);
      setInviteMailFailed(mailFailed);
      if (res.data.invite_accept_url) {
        setInviteAcceptUrl(res.data.invite_accept_url);
      }
      if (mailFailed) {
        // Conta ok, e-mail não — aviso amarelo, não toast de sucesso genérico.
        toast.warning(res.message || t("sa.tenants.new.inviteMailFailed"), {
          autoClose: 6000,
        });
      } else {
        toast.success(res.message || t("sa.tenants.new.success"), {
          autoClose: 2500,
        });
      }
    } catch (err: any) {
      const details = err?.details || err?.error?.details;
      if (details && typeof details === "object") {
        const mapped: Record<string, string> = {};
        Object.entries(details).forEach(([key, messages]) => {
          if (Array.isArray(messages) && messages[0]) {
            mapped[key] = String(messages[0]);
          }
        });
        setFieldErrors(mapped);
      }
      setError(err?.message || t("sa.tenants.new.error.save"));
    } finally {
      setSaving(false);
    }
  };

  // LAC-192/193: retry do convite na tela pós-create (mesmo endpoint da ficha).
  const onResendInvite = async () => {
    if (!created?.id) return;
    setResendBusy(true);
    try {
      const res = await resendSaOwnerInvite(created.id);
      if (!res?.success || !res.data) {
        toast.error(t("sa.tenants.new.inviteResendError"));
        return;
      }
      setCreated(res.data);
      if (res.data.invite_accept_url) {
        setInviteAcceptUrl(res.data.invite_accept_url);
      }
      const failed = Boolean(res.data.invite_mail?.failed);
      setInviteMailFailed(failed);
      if (failed) {
        toast.warning(res.message || t("sa.tenants.new.inviteMailFailed"), {
          autoClose: 5000,
        });
      } else {
        toast.success(res.message || t("sa.tenants.new.inviteResendOk"), {
          autoClose: 2500,
        });
      }
    } catch (err: any) {
      toast.error(err?.message || t("sa.tenants.new.inviteResendError"));
    } finally {
      setResendBusy(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteAcceptUrl) return;
    try {
      await navigator.clipboard.writeText(inviteAcceptUrl);
      toast.success(t("sa.tenants.new.inviteLinkCopied"), { autoClose: 1500 });
    } catch {
      toast.error(t("sa.tenants.new.copyFailed"));
    }
  };

  const openDetailPath = created
    ? `/super-admin/tenants/${created.id}${
        createdViaInvite ? "?highlight=invite" : ""
      }`
    : "/super-admin/tenants";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.tenants.new.title")}
          pageTitle={t("sa.menu.tenants")}
        />
        <ToastContainer />

        <div className="mb-3">
          <Button color="light" size="sm" tag={Link} to="/super-admin/tenants">
            {t("sa.tenants.detail.back")}
          </Button>
        </div>

        {error && (
          <Alert color="danger" className="d-flex justify-content-between gap-2">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => setError(null)}>
              {t("sa.tenants.new.dismiss")}
            </Button>
          </Alert>
        )}

        {created ? (
          <Card>
            <CardHeader>
              <h5 className="mb-0">{t("sa.tenants.new.doneTitle")}</h5>
            </CardHeader>
            <CardBody>
              {inviteMailFailed ? (
                <Alert color="warning" className="mb-3">
                  <p className="mb-2">{t("sa.tenants.new.inviteMailFailedHint")}</p>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color="warning"
                      size="sm"
                      disabled={resendBusy}
                      onClick={() => void onResendInvite()}
                    >
                      {resendBusy ? (
                        <Spinner size="sm" />
                      ) : (
                        t("sa.tenants.new.resendInvite")
                      )}
                    </Button>
                    {inviteAcceptUrl && (
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={() => void copyInviteLink()}
                      >
                        {t("sa.tenants.new.copyInviteLink")}
                      </Button>
                    )}
                  </div>
                  {inviteAcceptUrl && (
                    <div className="mt-2 text-break">
                      <code className="small">{inviteAcceptUrl}</code>
                    </div>
                  )}
                </Alert>
              ) : (
                <Alert color="success" className="mb-3">
                  {createdViaInvite
                    ? t("sa.tenants.new.doneHintInvite")
                    : t("sa.tenants.new.doneHint")}
                </Alert>
              )}
              {createdViaInvite && !inviteMailFailed && inviteAcceptUrl && (
                <div className="mb-3">
                  <Button
                    color="light"
                    size="sm"
                    className="me-2"
                    onClick={() => void copyInviteLink()}
                  >
                    {t("sa.tenants.new.copyInviteLink")}
                  </Button>
                  <small className="text-muted">
                    {t("sa.tenants.new.inviteQueuedHint")}
                  </small>
                </div>
              )}
              <p className="mb-1">
                <strong>{t("sa.tenants.new.fields.name")}:</strong> {created.name}
              </p>
              <p className="mb-1">
                <strong>{t("sa.tenants.channel.label")}:</strong>{" "}
                {created.billing_channel === "brdominio"
                  ? t("sa.tenants.channel.brdominio")
                  : t("sa.tenants.channel.selfServe")}
              </p>
              <p className="mb-3">
                <strong>{t("sa.tenants.detail.plan", {
                  name: created.subscription?.plan_name || "—",
                })}</strong>
                {" · "}
                {t("sa.tenants.new.quotaPreview", {
                  quota: created.subscription?.email_quota ?? 0,
                })}
              </p>

              <FormGroup>
                <Label>{t("sa.tenants.new.fields.ownerEmail")}</Label>
                <InputGroup>
                  <Input readOnly value={ownerEmail.trim().toLowerCase()} />
                  <Button
                    color="light"
                    type="button"
                    onClick={() =>
                      copyText("email", ownerEmail.trim().toLowerCase())
                    }
                  >
                    {copiedField === "email"
                      ? t("sa.tenants.new.copiedShort")
                      : t("sa.tenants.new.copy")}
                  </Button>
                </InputGroup>
              </FormGroup>
              {!createdViaInvite && (
                <FormGroup>
                  <Label>{t("sa.tenants.new.fields.ownerPassword")}</Label>
                  <InputGroup>
                    <Input readOnly value={ownerPassword} />
                    <Button
                      color="light"
                      type="button"
                      onClick={() => copyText("password", ownerPassword)}
                    >
                      {copiedField === "password"
                        ? t("sa.tenants.new.copiedShort")
                        : t("sa.tenants.new.copy")}
                    </Button>
                  </InputGroup>
                  <small className="text-muted">
                    {t("sa.tenants.new.passwordOnceHint")}
                  </small>
                </FormGroup>
              )}

              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button
                  color="primary"
                  onClick={() => navigate(openDetailPath)}
                >
                  {t("sa.tenants.new.openDetail")}
                </Button>
                <Button
                  color="light"
                  onClick={() => navigate("/super-admin/tenants")}
                >
                  {t("sa.tenants.new.backToList")}
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <h5 className="mb-0">{t("sa.tenants.new.formTitle")}</h5>
            </CardHeader>
            <CardBody>
              {loadingPlans ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              ) : (
                <Form onSubmit={onSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="tenant-name">
                          {t("sa.tenants.new.fields.name")}
                        </Label>
                        <Input
                          id="tenant-name"
                          value={name}
                          invalid={!!fieldErrors.name}
                          onChange={(e) => setName(e.target.value)}
                        />
                        <FormFeedback>{fieldErrors.name}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="billing-channel">
                          {t("sa.tenants.channel.label")}
                        </Label>
                        <Input
                          id="billing-channel"
                          type="select"
                          value={billingChannel}
                          onChange={(e) =>
                            setBillingChannel(
                              e.target.value as "brdominio" | "self_serve"
                            )
                          }
                        >
                          <option value="brdominio">
                            {t("sa.tenants.channel.brdominio")}
                          </option>
                          <option value="self_serve">
                            {t("sa.tenants.channel.selfServe")}
                          </option>
                        </Input>
                        <small className="text-muted">
                          {billingChannel === "brdominio"
                            ? t("sa.tenants.channel.brdominioHint")
                            : t("sa.tenants.channel.selfServeHint")}
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  {billingChannel === "brdominio" && (
                    <Alert color="info" className="mb-3">
                      <div className="fw-medium mb-1">
                        {t("sa.tenants.new.nsChecklist.title")}
                      </div>
                      <ol className="small mb-2 ps-3">
                        <li>{t("sa.tenants.new.nsChecklist.step1")}</li>
                        <li>{t("sa.tenants.new.nsChecklist.step2")}</li>
                        <li>{t("sa.tenants.new.nsChecklist.step3")}</li>
                      </ol>
                      <Link
                        to="/super-admin/dns/ns-guide"
                        className="small"
                      >
                        {t("sa.menu.dnsNsGuide")}
                      </Link>
                    </Alert>
                  )}

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="owner-name">
                          {t("sa.tenants.new.fields.ownerName")}
                        </Label>
                        <Input
                          id="owner-name"
                          value={ownerName}
                          invalid={!!fieldErrors.owner_name}
                          onChange={(e) => setOwnerName(e.target.value)}
                        />
                        <FormFeedback>{fieldErrors.owner_name}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="owner-email">
                          {t("sa.tenants.new.fields.ownerEmail")}
                        </Label>
                        <Input
                          id="owner-email"
                          type="email"
                          value={ownerEmail}
                          invalid={!!fieldErrors.owner_email}
                          onChange={(e) => setOwnerEmail(e.target.value)}
                        />
                        <FormFeedback>{fieldErrors.owner_email}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* LAC-074: checkbox para enviar convite por e-mail. */}
                  <FormGroup check className="mb-3">
                    <Input
                      id="send-invite"
                      type="checkbox"
                      checked={sendInvite}
                      onChange={(e) => {
                        setSendInvite(e.target.checked);
                        if (e.target.checked) {
                          // Limpa senha quando habilita convite.
                          setOwnerPassword("");
                        } else {
                          // Gera nova senha temporária quando desabilita.
                          setOwnerPassword(generateTempPassword());
                        }
                      }}
                    />
                    <Label check for="send-invite">
                      {t("sa.tenants.new.sendInvite")}
                    </Label>
                    <small className="d-block text-muted mt-1">
                      {t("sa.tenants.new.sendInviteHint")}
                    </small>
                  </FormGroup>

                  {/* Mostrar campo de senha apenas se não enviar convite */}
                  {!sendInvite && (
                    <FormGroup>
                      <Label for="owner-password">
                        {t("sa.tenants.new.fields.ownerPassword")}
                      </Label>
                      <InputGroup>
                        <Input
                          id="owner-password"
                          type={showPassword ? "text" : "password"}
                          value={ownerPassword}
                          invalid={!!fieldErrors.owner_password}
                          onChange={(e) => setOwnerPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                        <Button
                          color="light"
                          type="button"
                          title={
                            showPassword
                              ? t("sa.tenants.new.hidePassword")
                              : t("sa.tenants.new.showPassword")
                          }
                          aria-label={
                            showPassword
                              ? t("sa.tenants.new.hidePassword")
                              : t("sa.tenants.new.showPassword")
                          }
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          <i
                            className={
                              showPassword
                                ? "ri-eye-off-line"
                                : "ri-eye-line"
                            }
                          />
                        </Button>
                        <Button
                          color="light"
                          type="button"
                          onClick={() => setOwnerPassword(generateTempPassword())}
                        >
                          {t("sa.tenants.new.generatePassword")}
                        </Button>
                        <Button
                          color="light"
                          type="button"
                          onClick={() => copyText("password", ownerPassword)}
                        >
                          {t("sa.tenants.new.copy")}
                        </Button>
                      </InputGroup>
                      <FormFeedback className="d-block">
                        {fieldErrors.owner_password}
                      </FormFeedback>
                      <small className="text-muted">
                        {t("sa.tenants.new.passwordHint")}
                      </small>
                    </FormGroup>
                  )}

                  <FormGroup>
                    <Label for="plan-id">{t("sa.tenants.new.fields.plan")}</Label>
                    <Input
                      id="plan-id"
                      type="select"
                      value={planId}
                      invalid={!!fieldErrors.plan_id}
                      onChange={(e) =>
                        handlePlanChange(
                          e.target.value ? Number(e.target.value) : ""
                        )
                      }
                    >
                      <option value="">{t("sa.tenants.new.planPlaceholder")}</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.visibility === "private" ? ` [${t("sa.plans.visibility.private")}]` : ""}
                          {p.visibility === "internal" ? ` [${t("sa.plans.visibility.internal")}]` : ""}
                          {" — "}
                          {t("sa.tenants.new.quotaPreview", {
                            quota: p.email_quota,
                          })}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{fieldErrors.plan_id}</FormFeedback>
                    {selectedPlan && (
                      <Alert color="info" className="mt-2 mb-0 py-2 d-flex align-items-center gap-2">
                        <span>
                          {t("sa.tenants.new.planConfirm", {
                            name: selectedPlan.name,
                            quota: selectedPlan.email_quota.toLocaleString(),
                          })}
                        </span>
                        {visibilityBadge(selectedPlan.visibility)}
                      </Alert>
                    )}
                  </FormGroup>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="document">
                          {t("sa.tenants.new.fields.document")}
                        </Label>
                        <Input
                          id="document"
                          value={taxDocument}
                          onChange={(e) => setTaxDocument(e.target.value)}
                          placeholder={t("sa.tenants.new.optional")}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="phone">
                          {t("sa.tenants.new.fields.phone")}
                        </Label>
                        <Input
                          id="phone"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder={t("sa.tenants.new.optional")}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex flex-wrap gap-2">
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? (
                        <Spinner size="sm" />
                      ) : (
                        t("sa.tenants.new.submit")
                      )}
                    </Button>
                    <Button
                      color="light"
                      type="button"
                      tag={Link}
                      to="/super-admin/tenants"
                    >
                      {t("sa.tenants.detail.actions.cancel")}
                    </Button>
                  </div>
                </Form>
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default SuperAdminTenantNew;
